import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider } from '@mantine/core';
import { Context as StoreContext } from '../../../providers/StoreContextProvider';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import { triggerNotification } from '../../../helpers/notification';
import AppText from '../../common/AppText';
import AppCheckbox from '../../common/AppCheckbox';
import {
  ECOM_ORDER_PRODUCT_STATUS_ENUM,
  ECOM_ORDER_STATUS_ENUM
} from '../../../config/constants';

const StoreOrderUnsubmitModal = ({
  isOpen,
  onClose,
  onSuccess,
  pkEcomOrder
}) => {
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchStoreOrder,
    unsubmitBulkEcomOrderFromVendor
  } = useContext(StoreContext);
  const [formState, setFormState] = useState({
    skipNotification: false,
    loading: false
  });
  const { loading, value: ecomOrderState } = state.ecomOrder;

  const ecomOrder =
    pkEcomOrder?.toString() === ecomOrderState?.pkEcomOrder.toString()
      ? ecomOrderState
      : null;
  const canUnsubmit =
    ecomOrder &&
    ecomOrder.fkEcomOrderStatus.toString() ===
      ECOM_ORDER_STATUS_ENUM.SUCCEEDED.toString() &&
    ecomOrder.ecomOrderProductBulk.every(
      (p) =>
        p.fkEcomOrderProductStatus.toString() !==
          ECOM_ORDER_PRODUCT_STATUS_ENUM.ACCEPTED.toString() &&
        p.fkEcomOrderProductStatus.toString() !==
          ECOM_ORDER_PRODUCT_STATUS_ENUM.SHIPPED.toString() &&
        p.fkEcomOrderProductStatus.toString() !==
          ECOM_ORDER_PRODUCT_STATUS_ENUM.DELIVERED.toString()
    );

  useEffect(() => {
    if (isOpen) {
      if (!ecomOrder && pkEcomOrder) {
        fetchStoreOrder(authState.pkEcomStore, { pkEcomOrder }, null, (e) => {
          triggerNotification(e);
        });
      }

      setFormState({
        skipNotification: false,
        loading: false
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={{
        isSubmitHidden: !canUnsubmit,
        isLoading: formState.loading,
        onCancel: onClose,
        submitColor: 'red',
        submitTitle: 'Yes, unsubmit order',
        onSubmit: () => {
          setFormState({ ...formState, loading: true });
          unsubmitBulkEcomOrderFromVendor(
            authState.pkEcomStore,
            {
              pkEcomOrder,
              skipNotification: formState.skipNotification
            },
            () => {
              triggerNotification(
                `Order #${pkEcomOrder} has been unsubmitted`,
                'success'
              );

              onClose();
              if (onSuccess) {
                onSuccess();
              }
            },
            (e) => {
              setFormState({ ...formState, loading: false });
              triggerNotification(e);
            }
          );
        }
      }}
      isLoading={loading}
      isOpen={isOpen}
      onClose={onClose}
      title={`Confirm unsubmission of order #${pkEcomOrder}`}
    >
      {canUnsubmit ? (
        <AppStack style={{ gap: 20 }}>
          <AppText style={{ fontSize: 14 }}>
            Are you sure you want to unsubmit order{' '}
            <span style={{ fontWeight: 500 }}>#{pkEcomOrder}</span>?
          </AppText>

          <AppText style={{ fontSize: 14 }}>
            This will set the order back to the <span>accumulating</span> stage.
          </AppText>

          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              justifyContent: 'center',
              fontStyle: 'italic'
            }}
          >
            You can resubmit it later once updates are complete.
          </AppText>

          <Divider />

          <AppCheckbox
            checked={!formState.skipNotification}
            disabled={formState.loading}
            label="Send a notification email to the vendor"
            onChange={() =>
              setFormState({
                ...formState,
                skipNotification: !formState.skipNotification
              })
            }
            styles={{ label: { fontWeight: 500 } }}
          />
        </AppStack>
      ) : (
        <AppStack style={{ gap: 16 }}>
          <AppText style={{ fontSize: 14, fontWeight: 500 }}>
            Unable to unsubmit order #{pkEcomOrder}.
          </AppText>
          <AppText style={{ fontSize: 14 }}>
            It has already been processed or contains products that have been
            accepted, shipped, or delivered.
          </AppText>
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

StoreOrderUnsubmitModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  pkEcomOrder: PropTypes.number
};

export default StoreOrderUnsubmitModal;
